/**
 * BlockErrorBoundary - Catches render errors in block visual components
 *
 * Prevents a single malformed block from crashing the entire block tree.
 * On error, renders a fallback UI showing the block type and error message,
 * styled similarly to UnknownBlockVisual.
 *
 * Must be a class component because React error boundaries require
 * componentDidCatch / getDerivedStateFromError lifecycle methods.
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react'

interface BlockErrorBoundaryProps {
	/** Block type for error display */
	blockType: string
	/** Block ID for debugging */
	blockId: string
	children: ReactNode
}

interface BlockErrorBoundaryState {
	hasError: boolean
	error: Error | null
}

/**
 * Error boundary that wraps block renderers.
 * Catches render errors and displays a recoverable fallback UI.
 */
export class BlockErrorBoundary extends Component<BlockErrorBoundaryProps, BlockErrorBoundaryState> {
	constructor(props: BlockErrorBoundaryProps) {
		super(props)
		this.state = { hasError: false, error: null }
	}

	static getDerivedStateFromError(error: Error): BlockErrorBoundaryState {
		return { hasError: true, error }
	}

	override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error(
			`BlockErrorBoundary caught error in block type="${this.props.blockType}" id="${this.props.blockId}":`,
			error,
			errorInfo.componentStack
		)
	}

	override render(): ReactNode {
		if (this.state.hasError) {
			return (
				<div
					style={{
						padding: '1rem',
						border: '2px dashed var(--destructive)',
						borderRadius: '0.5rem',
						backgroundColor: 'color-mix(in oklch, var(--destructive) 10%, transparent)',
						color: 'var(--destructive)',
						fontFamily: 'monospace',
						fontSize: '0.875rem',
					}}
					data-testid="block-error-boundary"
					data-block-type={this.props.blockType}
					data-block-id={this.props.blockId}
				>
					<div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
						Render error in block: {this.props.blockType}
					</div>
					<div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
						{this.state.error?.message ?? 'Unknown error'}
					</div>
				</div>
			)
		}

		return this.props.children
	}
}
